import { type FC, useEffect, useMemo, useState } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { DEFAULT_PAGE_SIZE } from '../../../common/constants'
import { useSharedUserOptions } from '../../../common/useSharedUsers'
import { MultiSelectDropdown, type MultiSelectOption } from '../../../components/MultiSelectDropdown/MultiSelectDropdown'
import { SelectDropdown, type SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { type SchedulingFilters, type SchedulingEventType } from '../../../models/Scheduling'
import { useGetExternalUsersQuery } from '../../../store/externalUser/externalUser'

interface SchedulingFilterBarProps {
  eventTypes: SchedulingEventType[]
  filters: SchedulingFilters
  onFiltersChange: (filters: SchedulingFilters) => void
}

// Owner / contact / event type dropdowns + free-text search; the filters become server-side
// query params on the bookings request. Owner options are the users sharing scheduling with
// the current user (plus themselves, selected by default); contact options come from the
// clients (external users) API, searchable by name.
export const SchedulingFilterBar: FC<SchedulingFilterBarProps> = ({ eventTypes, filters, onFiltersChange }) => {
  const {
    options: ownerOptions,
    currentUser,
    sharedUserIds,
    toOption,
    loading: ownersLoading,
    onSearchInputChange: onOwnerSearchInputChange,
  } = useSharedUserOptions({ shareType: 'scheduling' })

  // The picked owner option is captured as local state because filters.userIds only holds ids
  // and the labels live in the live options, which a search term narrows — re-deriving the
  // selection from them would retrigger SelectDropdown's initialSelectedOption reset and
  // clobber the search input. If the captured owner's share gets revoked, the display falls
  // back to the current user to match the filter reconciliation below.
  const [ownerOption, setOwnerOption] = useState<SelectOption>(() => toOption(currentUser))
  const displayedOwnerOption = useMemo(
    () => (sharedUserIds == null || sharedUserIds.has(ownerOption.id) ? ownerOption : toOption(currentUser)),
    [sharedUserIds, ownerOption, currentUser, toOption],
  )

  // Drop selected owners who no longer share scheduling with the current user (e.g. the share
  // was revoked) and fall back to the current user when none remain, so the visible selection
  // matches the filtering.
  useEffect(() => {
    if (sharedUserIds == null || filters.userIds == null || filters.userIds.length === 0) {
      return
    }
    const validUserIds = filters.userIds.filter((id) => sharedUserIds.has(id))
    if (validUserIds.length !== filters.userIds.length) {
      onFiltersChange({ ...filters, userIds: validUserIds.length > 0 ? validUserIds : [currentUser.id] })
    }
  }, [sharedUserIds, filters, currentUser, onFiltersChange])

  const [contactSearch, setContactSearch] = useState('')
  const {
    data: contactsResponse,
    isFetching: contactsLoading,
    isLoading: contactsInitialLoading,
  } = useGetExternalUsersQuery(
    { accountId: currentUser.accountId, name: contactSearch.trim() || undefined, orderBy: 'lastName', pageRequest: { page: 0, size: DEFAULT_PAGE_SIZE } },
    { refetchOnMountOrArgChange: true },
  )
  const contactOptions: MultiSelectOption[] = useMemo(
    () => (contactsResponse?.data ?? []).map((contact) => ({ id: contact.id, label: getUserDisplayName(contact) })),
    [contactsResponse],
  )
  const eventTypeOptions: SelectOption[] = useMemo(() => eventTypes.map((eventType) => ({ id: eventType.id, label: eventType.name })), [eventTypes])

  const selectedOption = (options: SelectOption[], id: string | undefined): SelectOption | null => options.find((option) => option.id === id) ?? null

  return (
    <Row className='g-2'>
      <Col>
        <div className='paragraph-small zocks-text-gray-600 mb-1'>Owner</div>
        <SelectDropdown
          options={ownerOptions}
          initialSelectedOption={displayedOwnerOption}
          placeholder='Search team members'
          searchable
          loading={ownersLoading}
          inputAutoComplete='off'
          onSearchInputChange={onOwnerSearchInputChange}
          onOptionSelected={(option) => {
            setOwnerOption(option)
            onFiltersChange({ ...filters, userIds: [option.id] })
          }}
          onClear={() => {
            setOwnerOption(toOption(currentUser))
            onFiltersChange({ ...filters, userIds: [currentUser.id] })
          }}
        />
      </Col>
      <Col>
        <div className='paragraph-small zocks-text-gray-600 mb-1'>Filter by contact</div>
        <MultiSelectDropdown
          name='schedulingContactFilter'
          options={contactOptions}
          initialSelectedOptions={filters.contactIds ?? []}
          placeholder='All Contacts'
          clearable
          disabled={contactsInitialLoading}
          loading={contactsLoading}
          onSearchInputChange={setContactSearch}
          onSelectedChanged={(contactIds) => onFiltersChange({ ...filters, contactIds: contactIds.length ? contactIds : undefined })}
        />
      </Col>
      <Col>
        <div className='paragraph-small zocks-text-gray-600 mb-1'>Filter by event type</div>
        <SelectDropdown
          options={eventTypeOptions}
          initialSelectedOption={selectedOption(eventTypeOptions, filters.eventTypeIds?.[0])}
          placeholder='All event types'
          showDeselectOption
          deselectOptionLabel='All event types'
          onOptionSelected={(option) => onFiltersChange({ ...filters, eventTypeIds: [option.id] })}
          onDeselect={() => onFiltersChange({ ...filters, eventTypeIds: undefined })}
        />
      </Col>
      <Col>
        <div className='paragraph-small zocks-text-gray-600 mb-1'>Search</div>
        <div className='d-flex align-items-center zocks-gap-1.25 border rounded-1 zocks-border-color-gray-500 zocks-px-0.75'>
          <i className='bi bi-search' />
          <Form.Control
            type='text'
            value={filters.search ?? ''}
            onChange={(event) => onFiltersChange({ ...filters, search: event.currentTarget.value || undefined })}
            placeholder='Search'
            aria-label='Search bookings'
            className='border-0 px-0'
          />
        </div>
      </Col>
    </Row>
  )
}
